/**
 * ToggleSwitch control for overriding a profile user's display name.
 *
 * Renders a labelled `ToggleSwitch`; when enabled, reveals a `ProfileOveride`
 * text field pre-filled with any existing `nameOverride` value. Saving or
 * clearing persists the change via `profiles.manage.update`, and toggling off
 * when no override is saved immediately collapses the field without a server
 * round-trip.
 */
import { PadBox } from 'component/basics';
import { ToggleSwitch } from 'component/form';
import { useProfileManage } from 'data/profile';
import { updateProfileManageAsync } from 'data/profiles';
import { useState } from 'react';
import { View } from 'react-native';
import { useDatastore, useInstanceKey } from 'util/datastore';
import { t } from 'util/stdtypes';

import { ProfileOveride } from './profile-override';

export function ProfileNameOverride() {
    const instanceKey = useInstanceKey();
    const { profile, isLoading } = useProfileManage(instanceKey);
    if (isLoading) {
        return null;
    }
    return <ProfileNameOverrideForm existingOverride={profile?.overrideName ?? null} profileKey={instanceKey} />;
}

function ProfileNameOverrideForm({ existingOverride, profileKey }: { existingOverride: string | null; profileKey: string }) {
    const datastore = useDatastore();
    const [overrideEnabled, setOverrideEnabled] = useState<boolean>(!!existingOverride);

    async function saveOverrideAsync(value: string) {
        await updateProfileManageAsync(datastore, { profileKey, overrideName: value || null });
    }

    async function clearOverrideAsync() {
        await updateProfileManageAsync(datastore, { profileKey, overrideName: null });
    }

    async function onToggleAsync(enabled: boolean) {
        setOverrideEnabled(enabled);
        if (!enabled) {
            await clearOverrideAsync();
        }
    }

    return (
        <View>
            <ToggleSwitch
                label={t('profile.edit.override_name_title', 'Override profile name')}
                onChange={onToggleAsync}
                spread
                testID='override-profile-name'
                value={overrideEnabled}
            />
            {overrideEnabled && (
                <PadBox top={16}>
                    <ProfileOveride
                        label={t('profile.edit.name_override', 'Name')}
                        onCancel={() => {
                            if (!existingOverride) {
                                setOverrideEnabled(false);
                            }
                        }}
                        onSave={saveOverrideAsync}
                        placeholder={t('profile.edit.name_override.placeholder', 'Add your override name...')}
                        previousValue={existingOverride ?? ''}
                        testID='name-override-field'
                    />
                </PadBox>
            )}
        </View>
    );
}
